import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Alert,
  CircularProgress,
  Grid,
  Paper,
  alpha,
} from '@mui/material';
import { PageLayout, PageHeader, ContentSection } from '../components/layout/PageLayout';
import { EnhancedCard, StatsCard } from '../components/layout/EnhancedCard';
import { encryptSessionData, decryptSessionData, type SessionData } from '../services/exileguardService';
import SecurityIcon from '@mui/icons-material/Security';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import VpnKeyIcon from '@mui/icons-material/VpnKey';

const ARBITRARY_KEY = 'nqwdqw634nj132512nd12e';

interface ProcessingResult {
  key: string;
  name: string;
  error?: string;
}

const ExileGuardPage: React.FC = () => {
  const [lines, setLines] = useState('');
  const [instructions, setInstructions] = useState('');
  const [durationDays, setDurationDays] = useState(7);
  const [durationHours, setDurationHours] = useState(0);
  const [results, setResults] = useState<ProcessingResult[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [encryptedKeyInput, setEncryptedKeyInput] = useState(''); // manual decrypt input
  const [decryptedData, setDecryptedData] = useState<SessionData | null>(null); // result of successful decrypt
  const [isDecrypting, setIsDecrypting] = useState(false); // decrypt processing state
  const [decryptError, setDecryptError] = useState<string | null>(null); // decrypt error message

  const onGenerate = async () => {
    setIsProcessing(true);
    const out: ProcessingResult[] = [];

    const inputLines = lines
      .split(/\r?\n/)
      .map(l => l.trim())
      .filter(l => l);

    for (let idx = 0; idx < inputLines.length; idx++) {
      const line = inputLines[idx];
      const parts = line.split(':');

      if (parts.length !== 7) {
        out.push({
          key: '',
          name: '',
          error: `Line ${idx + 1}: expected 7 fields (email:password:username:proxyIp:proxyPort:proxyUsername:proxyPassword), got ${
            parts.length
          }`,
        });
        continue;
      }

      const [email, password, charName, proxyIP, proxyPort, proxyUser, proxyPass] = parts;

      // Validate required fields
      if (!email || !password || !charName || !proxyIP || !proxyPort) {
        out.push({
          key: '',
          name: charName || `Line ${idx + 1}`,
          error: `Line ${idx + 1}: missing required fields (email, password, username, proxyIp, proxyPort)`,
        });
        continue;
      }

      const sd: SessionData = {
        email,
        password,
        instruction: `${charName} - ${instructions}`,
        endDate: Date.now() + (durationDays * 24 + durationHours) * 60 * 60 * 1000,
        proxyIP,
        proxyPort,
        proxyUsername: proxyUser,
        proxyPassword: proxyPass,
      };

      try {
        // Generate key using backend API
        const key = await encryptSessionData(sd, ARBITRARY_KEY);

        // Verify by decrypting using backend API
        const decrypted = await decryptSessionData(key, ARBITRARY_KEY);

        // Verify round-trip integrity
        const verified =
          decrypted.email === sd.email &&
          decrypted.password === sd.password &&
          decrypted.instruction === sd.instruction &&
          decrypted.proxyIP === sd.proxyIP &&
          decrypted.proxyPort === sd.proxyPort &&
          decrypted.proxyUsername === sd.proxyUsername &&
          decrypted.proxyPassword === sd.proxyPassword;

        if (!verified) {
          throw new Error('Verification failed - decrypted data does not match original');
        }

        out.push({
          key,
          name: charName,
        });
      } catch (e) {
        out.push({
          key: '',
          name: charName,
          error: `Line ${idx + 1}: ${e instanceof Error ? e.message : String(e)}`,
        });
      }
    }

    setResults(out);
    setIsProcessing(false);
  };

  const handleClearResults = () => {
    setResults([]);
  };

  /**
   * Handles decryption of ExileGuard key input.
   * Trims input, calls decryptSessionData, updates state accordingly.
   * Inputs: reads encryptedKeyInput state.
   * Outputs: sets decryptedData on success, decryptError on failure, manages isDecrypting.
   * Error handling: Surfaces API/service errors via decryptError.
   * Note: Whitespace is trimmed from input; button disabled during processing.
   */
  const handleDecryptKey = async () => {
    setDecryptError(null);
    setIsDecrypting(true);
    try {
      const data = await decryptSessionData(encryptedKeyInput.trim(), ARBITRARY_KEY);
      setDecryptedData(data);
    } catch (e) {
      setDecryptError(e instanceof Error ? e.message : String(e));
      setDecryptedData(null);
    } finally {
      setIsDecrypting(false);
    }
  };

  const successCount = results.filter(r => r.key && !r.error).length;
  const errorCount = results.filter(r => r.error).length;

  return (
    <PageLayout>
      <PageHeader
        icon={<SecurityIcon sx={{ fontSize: 28 }} />}
        title='ExileGuard Key Generator'
        subtitle='Generate encrypted keys for secure bot deployment and account management'
      />
      <ContentSection title='Bulk Input Configuration'>
        <EnhancedCard variant='outlined' sx={{ p: 3, mb: 3 }}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 3,
              backgroundColor: alpha('#3498DB', 0.1),
              border: `1px solid ${alpha('#3498DB', 0.2)}`,
              borderRadius: 2,
            }}
          >
            <Typography variant='subtitle2' color='primary' sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <VpnKeyIcon sx={{ fontSize: 18 }} />
              Input Format
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Enter one entry per line in the format:
              <br />
              <Typography
                component='code'
                sx={{
                  backgroundColor: alpha('#000', 0.3),
                  padding: '4px 8px',
                  borderRadius: 1,
                  fontSize: '0.875rem',
                  fontFamily: 'monospace',
                  display: 'inline-block',
                  mt: 1,
                }}
              >
                email:password:username:proxyIp:proxyPort:proxyUsername:proxyPassword
              </Typography>
            </Typography>
          </Paper>
          <TextField
            label='Account Data (One per line)'
            multiline
            minRows={6}
            maxRows={12}
            fullWidth
            value={lines}
            onChange={e => setLines(e.target.value)}
            placeholder='example@email.com:mypassword:CharacterName:192.168.1.1:8080:proxyuser:proxypass'
            sx={{ mb: 3 }}
          />

          <Box sx={{ mb: 3 }}>
            <Typography variant='h6' gutterBottom>
              Additional Instructions
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
              These instructions will be combined with the character name in the generated key.
            </Typography>
            <TextField
              label='Instructions'
              multiline
              minRows={3}
              maxRows={6}
              fullWidth
              value={instructions}
              onChange={e => setInstructions(e.target.value)}
              placeholder='Enter additional instructions here...'
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant='h6' gutterBottom>
              Key Duration
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
              How long the generated keys will remain valid.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label='Days'
                type='number'
                value={durationDays}
                onChange={e => setDurationDays(Math.max(0, Math.min(365, Number(e.target.value) || 0)))}
                slotProps={{ htmlInput: { min: 0, max: 365 } }}
                sx={{ width: 120 }}
              />
              <TextField
                label='Hours'
                type='number'
                value={durationHours}
                onChange={e => setDurationHours(Math.max(0, Math.min(23, Number(e.target.value) || 0)))}
                slotProps={{ htmlInput: { min: 0, max: 23 } }}
                sx={{ width: 120 }}
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant='contained' onClick={onGenerate} disabled={isProcessing || !lines.trim()} sx={{ minWidth: 140 }}>
              {isProcessing ? <CircularProgress size={20} /> : 'Generate Keys'}
            </Button>
            {results.length > 0 && (
              <Button variant='outlined' onClick={handleClearResults} disabled={isProcessing}>
                Clear Results
              </Button>
            )}
          </Box>
        </EnhancedCard>
      </ContentSection>
      /** "Decrypt ExileGuard Key" functionality: Purpose: Internal-only utility to inspect ExileGuard keys. Data flow: Uses
      decryptSessionData() with ARBITRARY_KEY. Security note: Not intended for external exposure beyond internal admin panel; avoid logging
      decrypted data server-side; this renders only for operators. Usage: Paste key, click "Decrypt Key", full SessionData is shown as
      prettified JSON. */
      <ContentSection title='Decrypt ExileGuard Key'>
        <EnhancedCard variant='outlined' sx={{ p: 3 }}>
          <TextField
            label='ExileGuard Key to Decrypt'
            multiline
            minRows={4}
            maxRows={8}
            fullWidth
            value={encryptedKeyInput}
            onChange={e => {
              setEncryptedKeyInput(e.target.value);
              setDecryptedData(null);
              setDecryptError(null);
            }}
            placeholder='Paste the encrypted key here...'
            sx={{ mb: 3 }}
          />

          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Button
              variant='contained'
              onClick={handleDecryptKey}
              disabled={isDecrypting || !encryptedKeyInput.trim()}
              sx={{ minWidth: 140 }}
            >
              {isDecrypting ? <CircularProgress size={20} /> : 'Decrypt Key'}
            </Button>
          </Box>

          {(decryptedData || decryptError || isDecrypting) && (
            <Box>
              {isDecrypting && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                  <CircularProgress size={40} />
                  <Typography variant='body1' sx={{ ml: 2 }}>
                    Decrypting key...
                  </Typography>
                </Box>
              )}
              {decryptError && (
                <Alert severity='error' sx={{ mb: 2 }}>
                  <Typography variant='body2'>Decryption Error: {decryptError}</Typography>
                </Alert>
              )}
              {decryptedData && (
                <Box>
                  <Typography variant='h6' gutterBottom>
                    Decrypted SessionData
                  </Typography>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      backgroundColor: alpha('#000', 0.1),
                      borderRadius: 1,
                      fontFamily: 'monospace',
                      overflow: 'auto',
                    }}
                  >
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '0.875rem' }}>{JSON.stringify(decryptedData, null, 2)}</pre>
                  </Paper>
                </Box>
              )}
            </Box>
          )}
        </EnhancedCard>
      </ContentSection>
      {results.length > 0 && (
        <>
          {/* Results Summary Stats */}
          <ContentSection title='Generation Summary'>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <StatsCard title='Total Processed' value={results.length} icon={<VpnKeyIcon />} color='#3498DB' />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <StatsCard title='Successful' value={successCount} icon={<CheckCircleIcon />} color='#27AE60' />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <StatsCard title='Errors' value={errorCount} icon={<ErrorIcon />} color='#E31B23' />
              </Grid>
            </Grid>
          </ContentSection>

          <ContentSection title='Generated Keys'>
            <EnhancedCard sx={{ p: 3 }}>
              {errorCount > 0 && (
                <Alert
                  severity='warning'
                  sx={{
                    mb: 2,
                    background: 'linear-gradient(135deg, rgba(243, 156, 18, 0.1), rgba(243, 156, 18, 0.05))',
                    border: `1px solid ${alpha('#F39C12', 0.3)}`,
                  }}
                >
                  <Typography variant='body2'>
                    <strong>{errorCount} entries failed to process.</strong> Failed entries are not shown in the results table below.
                  </Typography>
                </Alert>
              )}

              <TableContainer
                sx={{
                  maxHeight: 600,
                  border: `1px solid ${alpha('#3498DB', 0.2)}`,
                  borderRadius: 2,
                }}
              >
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 'bold',
                          width: '70%',
                          backgroundColor: alpha('#3498DB', 0.1),
                        }}
                      >
                        ExileGuard Key
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 'bold',
                          width: '30%',
                          backgroundColor: alpha('#3498DB', 0.1),
                        }}
                      >
                        Character Name
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {results
                      .filter(r => r.key && !r.error)
                      .map((result, i) => (
                        <TableRow
                          key={i}
                          sx={{
                            '&:hover': {
                              backgroundColor: alpha('#3498DB', 0.05),
                            },
                          }}
                        >
                          <TableCell
                            sx={{
                              fontFamily: 'monospace',
                              fontSize: '0.875rem',
                              wordBreak: 'break-all',
                              width: '70%',
                              backgroundColor: alpha('#000', 0.2),
                              border: `1px solid ${alpha('#3498DB', 0.1)}`,
                            }}
                          >
                            {result.key}
                          </TableCell>
                          <TableCell sx={{ width: '30%' }}>
                            <Typography variant='body2' fontWeight={500}>
                              {result.name}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Alert
                severity='info'
                sx={{
                  mt: 3,
                  background: 'linear-gradient(135deg, rgba(52, 152, 219, 0.1), rgba(52, 152, 219, 0.05))',
                  border: `1px solid ${alpha('#3498DB', 0.3)}`,
                }}
              >
                <Typography variant='body2'>
                  <strong>💡 Usage Tip:</strong> You can select and copy the table data above to paste directly into Google Sheets or Excel.
                  The "ExileGuard Key" column contains the encrypted keys, and "Character Name" shows the associated character names.
                  {errorCount > 0 && ` Note: ${errorCount} failed entries are excluded from this table.`}
                </Typography>
              </Alert>
            </EnhancedCard>
          </ContentSection>
        </>
      )}
    </PageLayout>
  );
};

export default ExileGuardPage;
